import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { QCStandardsListResult } from "@/modules/qc/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { qcApi } from "../api/client";
import {
  qcKeys,
  useDeleteQCStandard,
  useQCStandardsQuery,
} from "../api/queries";
import { QCTour, useQCTour } from "../tour/QCTour";
import { QCFilter } from "../ui/QCFilter";
import { QCTable } from "../ui/QCTable";
import { QCToolbar } from "../ui/QCToolbar";

export function QCListPage() {
  const nav = useNavigate({ from: "/app/qc" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/qc" });
  const qc = useQueryClient();
  const tour = useQCTour();

  const list = useQCStandardsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["qc:create"]);

  const activateMutation = useMutation({
    mutationFn: (id: string) => qcApi.update(id, { status: "active" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qcKeys.all });
      toast.success("ເປີດໃຊ້ງານສໍາເລັດ");
    },
    onError: () => {
      toast.error("ເປີດໃຊ້ງານລົ້ມເຫຼວ");
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => qcApi.update(id, { status: "inactive" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qcKeys.all });
      toast.success("ປິດໃຊ້ງານສໍາເລັດ");
    },
    onError: () => {
      toast.error("ປິດໃຊ້ງານລົ້ມເຫຼວ");
    },
  });

  const handleActivate = (qcItem: QCStandardsListResult["data"][number]) => {
    activateMutation.mutate(qcItem.id);
  };

  const handleDeactivate = (qcItem: QCStandardsListResult["data"][number]) => {
    deactivateMutation.mutate(qcItem.id);
  };

  const handleCopy = (id: string) => {
    nav({ to: "/app/qc/new", search: { copyFrom: id } });
  };

  const deleteMutation = useDeleteQCStandard();

  const handleDelete = (qcItem: QCStandardsListResult["data"][number]) => {
    deleteMutation.mutate(qcItem.id);
  };

  return (
    <>
      <Header />

      <Main>
        <QCTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <QCToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/qc/new" })}
          onStartTour={tour.startTour}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <QCFilter />

          <QCTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(qc) => nav({ to: `/app/qc/${qc.id}/edit` })}
            onView={(id) => nav({ to: `/app/qc/${id}` })}
            onCopy={handleCopy}
            onActivate={handleActivate}
            onDeactivate={handleDeactivate}
            onDelete={handleDelete}
            onRecordClick={(qc: QCStandardsListResult["data"][number]) => {
              nav({ to: `/app/qc/${qc.id}/record` });
            }}
          />
        </div>
      </Main>
    </>
  );
}
